import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { AlertCircle, ArrowLeft, Banknote, TrendingUp } from 'lucide-react'
import { apiJson, type Page } from '../api'
import { hasAnyRole } from '../auth'

interface Payslip {
  id: string
  employeeId: string
  employeeName: string
  employeeCode: string
  period: string
  baseSalary: number
  allowances: number
  deductions: number
  netPay: number
  currency: string
  status: 'draft' | 'issued' | 'paid'
  notes: string | null
}

interface Employee {
  id: string
  employeeCode: string
  firstName: string
  lastName: string
}

const emptyForm = { employeeId: '', period: '', baseSalary: '', allowances: '0', deductions: '0', status: 'issued', notes: '' }
type FormState = typeof emptyForm

const inputCls =
  'w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm outline-none transition-all placeholder:text-slate-400 focus:border-brand-400 focus:ring-4 focus:ring-brand-100'
const labelCls = 'mb-1.5 block text-xs font-semibold text-slate-500'

function money(n: number, currency: string) {
  return `${n.toLocaleString('en-US')} ${currency}`
}

export default function PayslipForm() {
  const { id } = useParams()
  const isEdit = Boolean(id)
  const navigate = useNavigate()
  const location = useLocation()
  const allowManage = hasAnyRole('admin', 'hr')

  const [employees, setEmployees] = useState<Employee[]>([])
  const [currency, setCurrency] = useState('MMK')
  const [form, setForm] = useState<FormState>(emptyForm)
  const [slipLabel, setSlipLabel] = useState('')
  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // currency + employees
  useEffect(() => {
    apiJson<{ currency?: string } | null>('/organization')
      .then((o) => setCurrency(o?.currency ?? 'MMK'))
      .catch(() => {})
    if (!allowManage) return
    apiJson<Page<Employee>>('/employees?size=500&sort=firstName&order=ASC')
      .then((p) => setEmployees(p.data))
      .catch(() => {})
  }, [allowManage])

  // prefill
  useEffect(() => {
    if (!isEdit) {
      const now = new Date()
      setForm({ ...emptyForm, period: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}` })
      return
    }
    const fromState = (location.state as { slip?: Payslip } | null)?.slip
    const apply = (slip: Payslip) => {
      setForm({
        employeeId: slip.employeeId,
        period: slip.period,
        baseSalary: String(slip.baseSalary),
        allowances: String(slip.allowances),
        deductions: String(slip.deductions),
        status: slip.status,
        notes: slip.notes ?? '',
      })
      setSlipLabel(`${slip.employeeName} · ${slip.period}`)
      setLoading(false)
    }
    if (fromState) {
      apply(fromState)
      return
    }
    // direct load / refresh: find the payslip in the admin list
    apiJson<Page<Payslip>>('/payslips?size=500')
      .then((p) => {
        const slip = p.data.find((s) => s.id === id)
        if (!slip) throw new Error('Payslip not found')
        apply(slip)
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load payslip')
        setLoading(false)
      })
  }, [id, isEdit, location.state])

  const netPreview = useMemo(
    () => (parseInt(form.baseSalary, 10) || 0) + (parseInt(form.allowances, 10) || 0) - (parseInt(form.deductions, 10) || 0),
    [form.baseSalary, form.allowances, form.deductions],
  )

  const save = async () => {
    setSaving(true)
    setError('')
    try {
      await apiJson('/payslips', {
        method: 'POST',
        body: JSON.stringify({
          employeeId: form.employeeId,
          period: form.period,
          baseSalary: parseInt(form.baseSalary, 10) || 0,
          allowances: parseInt(form.allowances, 10) || 0,
          deductions: parseInt(form.deductions, 10) || 0,
          status: form.status,
          ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
        }),
        headers: { 'Content-Type': 'application/json' },
      })
      navigate('/payroll')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed')
      setSaving(false)
    }
  }

  if (!allowManage) {
    return (
      <div className="flex items-center gap-2.5 rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-700">
        <AlertCircle size={16} className="shrink-0" />
        Only HR or admin users can manage payslips.
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-brand-600" />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <Link to="/payroll" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 transition-colors hover:text-brand-600">
        <ArrowLeft size={15} /> Back to Payroll
      </Link>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center gap-3.5 border-b border-slate-100 px-6 py-5">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-md shadow-brand-500/25">
            <Banknote size={20} />
          </div>
          <div>
            <h1 className="text-lg font-bold tracking-tight">{isEdit ? `Edit payslip — ${slipLabel}` : 'New Payslip'}</h1>
            <p className="text-xs text-slate-400">Upserts on employee + period; issued payslips notify the employee</p>
          </div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            save()
          }}
          className="space-y-4 px-6 py-6"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Employee *</label>
              <select
                required
                value={form.employeeId}
                onChange={(e) => setForm({ ...form, employeeId: e.target.value })}
                disabled={isEdit}
                className={`${inputCls} disabled:bg-slate-50 disabled:text-slate-400`}
              >
                <option value="">— Select employee —</option>
                {employees.map((e) => (
                  <option key={e.id} value={e.id}>{e.firstName} {e.lastName} ({e.employeeCode})</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Period *</label>
              <input
                required
                type="month"
                value={form.period}
                onChange={(e) => setForm({ ...form, period: e.target.value })}
                disabled={isEdit}
                className={`${inputCls} disabled:bg-slate-50 disabled:text-slate-400`}
              />
            </div>
            <div>
              <label className={labelCls}>Base salary ({currency}) *</label>
              <input required type="number" min={0} value={form.baseSalary} onChange={(e) => setForm({ ...form, baseSalary: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Status</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls}>
                <option value="draft">Draft</option>
                <option value="issued">Issued</option>
                <option value="paid">Paid</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Allowances ({currency})</label>
              <input type="number" min={0} value={form.allowances} onChange={(e) => setForm({ ...form, allowances: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Deductions ({currency})</label>
              <input type="number" min={0} value={form.deductions} onChange={(e) => setForm({ ...form, deductions: e.target.value })} className={inputCls} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Notes</label>
            <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} maxLength={500} placeholder="Optional note on the payslip…" className={inputCls} />
          </div>

          <p className="flex items-center gap-2 rounded-xl bg-brand-50 px-4 py-2.5 text-xs font-semibold text-brand-700">
            <TrendingUp size={14} />
            Net pay preview: {money(netPreview, currency)}
          </p>

          {error && (
            <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertCircle size={16} className="shrink-0" />
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-1">
            <Link
              to="/payroll"
              className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={saving || !form.employeeId || !form.period || !form.baseSalary}
              className="rounded-xl bg-gradient-to-r from-brand-600 to-brand-700 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand-500/25 transition-all hover:brightness-110 disabled:opacity-50"
            >
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create payslip'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
